import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
    ArrowLeft, ArrowRight, Building2, User, FileText, CheckCircle, MapPin, Truck, 
    Briefcase, Wrench, Calendar, ExternalLink, Package, Store, ShoppingCart, Recycle
} from 'lucide-react';

const RegisterPage = () => {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [sameAddress, setSameAddress] = useState(true);
    
    const [formData, setFormData] = useState({
        // Étape 1 - Entreprise
        company_name: '',
        siren: '',
        vat_number: '',
        country: '',
        city: '',
        
        // Étape 2 - Contact
        contact_name: '',
        email: '',
        phone: '',
        whatsapp: '',
        
        // Étape 3 - Activité et Besoins
        activity_types: [], // Multi-select
        activity_other: '',
        search_preference: '', // appareils_reparer, fonctionnels, les_deux
        repair_levels: [], // Multi-select: niveau_1, niveau_2, niveau_3, revente
        is_already_active: '', // oui, debute, nouveau_canal
        weekly_volume: '',
        points_of_sale: '',
        comments: '',
        
        // Étape 4 - Adresses
        billing_address: {
            street: '',
            street2: '',
            postal_code: '',
            city: '',
            country: 'FR'
        },
        shipping_same_as_billing: true,
        shipping_address: {
            name: '',
            street: '',
            street2: '',
            postal_code: '',
            city: '',
            country: 'FR'
        },
        
        // Étape 5 - Finaliser
        password: '',
        confirmPassword: '',
        calendly_booked: false,
        accept_contact: false,
        accept_cgv: false
    });

    const countries = [
        { code: 'FR', name: 'France' },
        { code: 'BE', name: 'Belgique' },
        { code: 'CH', name: 'Suisse' },
        { code: 'DE', name: 'Allemagne' },
        { code: 'ES', name: 'Espagne' },
        { code: 'IT', name: 'Italie' },
        { code: 'NL', name: 'Pays-Bas' },
        { code: 'PT', name: 'Portugal' },
        { code: 'LU', name: 'Luxembourg' },
        { code: 'GB', name: 'Royaume-Uni' },
        { code: 'DZ', name: 'Algérie' },
        { code: 'MA', name: 'Maroc' },
        { code: 'TN', name: 'Tunisie' },
        { code: 'OTHER', name: 'Autre' }
    ];

    const activityTypes = [
        { id: 'reparateur', label: 'Réparateur', description: 'Boutique de réparation', icon: Wrench },
        { id: 'grossiste', label: 'Grossiste / Revendeur', description: 'Achat-revente en volume', icon: Package },
        { id: 'ecommercant', label: 'E-commerçant', description: 'Vente en ligne', icon: ShoppingCart },
        { id: 'distributeur', label: 'Distributeur', description: 'Distribution multicanal', icon: Store },
        { id: 'recycleur', label: 'Recycleur / Reconditionneur', description: 'Traitement & reconditionnement', icon: Recycle },
        { id: 'autre', label: 'Autre', description: 'À préciser en commentaire', icon: Briefcase }
    ];

    const repairLevels = [
        { 
            id: 'niveau_1', 
            title: 'Niveau 1', 
            subtitle: 'Réparations courantes',
            items: ['Écran, batterie', 'Caméra, nappe', 'Boutons, haut-parleur']
        },
        { 
            id: 'niveau_2', 
            title: 'Niveau 2', 
            subtitle: 'Réparations avancées',
            items: ['Vitre arrière, chassis', 'Face ID / Touch ID', 'Pannes longues (sans micro-soudure)']
        },
        { 
            id: 'niveau_3', 
            title: 'Niveau 3', 
            subtitle: 'Micro-soudure / carte mère',
            items: ['Rebillage BGA', 'Remplacement de puces', 'Diagnostics avancés']
        },
        { 
            id: 'revente', 
            title: 'Revente', 
            subtitle: 'Sans réparation',
            items: ['Je revends en l\'état', 'Je sous-traite', 'Je recherche des lots']
        }
    ];

    const weeklyVolumes = [
        { value: 'moins_10', label: 'Moins de 10 appareils' },
        { value: '10_30', label: '10 à 30 appareils' },
        { value: '30_100', label: '30 à 100 appareils' },
        { value: 'plus_100', label: 'Plus de 100 appareils' }
    ];

    const pointsOfSale = [
        { value: '1', label: '1 point de vente' },
        { value: '2_5', label: '2 à 5 points de vente' },
        { value: 'plus_5', label: 'Plus de 5' },
        { value: 'pure_player', label: 'Pure player (pas de boutique physique)' }
    ];

    const toggleActivityType = (activityId) => {
        setFormData(prev => ({
            ...prev,
            activity_types: prev.activity_types.includes(activityId)
                ? prev.activity_types.filter(a => a !== activityId)
                : [...prev.activity_types, activityId]
        }));
    };

    const toggleRepairLevel = (levelId) => {
        setFormData(prev => ({
            ...prev,
            repair_levels: prev.repair_levels.includes(levelId)
                ? prev.repair_levels.filter(l => l !== levelId)
                : [...prev.repair_levels, levelId]
        }));
    };

    const updateBillingAddress = (field, value) => {
        setFormData(prev => ({
            ...prev,
            billing_address: { ...prev.billing_address, [field]: value }
        }));
    };

    const updateShippingAddress = (field, value) => {
        setFormData(prev => ({
            ...prev,
            shipping_address: { ...prev.shipping_address, [field]: value }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.accept_cgv) {
            toast.error('Veuillez accepter les conditions générales de vente');
            return;
        }
        
        if (formData.password !== formData.confirmPassword) {
            toast.error('Les mots de passe ne correspondent pas');
            return;
        }

        if (formData.password.length < 8) {
            toast.error('Le mot de passe doit contenir au moins 8 caractères');
            return;
        }

        setLoading(true);

        try {
            const { confirmPassword, ...submitData } = formData;
            submitData.registration_number = submitData.siren;
            submitData.shipping_same_as_billing = sameAddress;
            // Convert activity_types array to string for legacy compatibility
            submitData.activity_type = submitData.activity_types.join(', ');
            // Convert repair_levels array to string for storage
            submitData.repair_level = submitData.repair_levels.join(', ');
            // Store consent timestamp
            submitData.consent_timestamp = new Date().toISOString();
            submitData.consent_accept_contact = submitData.accept_contact;
            submitData.consent_accept_cgv = submitData.accept_cgv;
            
            await register(submitData);
            toast.success('Demande de compte envoyée ! Un chargé commercial pourra vous contacter pour finaliser l\'ouverture.', {
                duration: 8000,
                style: {
                    fontSize: '16px',
                    padding: '16px 24px'
                }
            });
            navigate('/login');
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur lors de l\'inscription';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    const validateStep1 = () => {
        if (!formData.company_name || !formData.siren || !formData.country) {
            toast.error('Veuillez remplir tous les champs obligatoires');
            return false;
        }
        if (formData.country === 'FR' && !/^\d{9,14}$/.test(formData.siren)) {
            toast.error('Le SIREN/SIRET doit contenir 9 à 14 chiffres');
            return false;
        }
        return true;
    };

    const validateStep2 = () => {
        if (!formData.contact_name || !formData.email || !formData.phone) {
            toast.error('Veuillez remplir tous les champs obligatoires');
            return false;
        }
        return true;
    };

    const validateStep3 = () => {
        if (formData.activity_types.length === 0) {
            toast.error('Veuillez sélectionner au moins un type d\'activité');
            return false;
        }
        if (!formData.search_preference) {
            toast.error('Veuillez indiquer ce que vous recherchez');
            return false;
        }
        if (formData.repair_levels.length === 0) {
            toast.error('Veuillez sélectionner au moins un niveau de réparation');
            return false;
        }
        return true;
    };

    const validateStep4 = () => {
        const { billing_address } = formData;
        if (!billing_address.street || !billing_address.postal_code || !billing_address.city) {
            toast.error('Veuillez remplir l\'adresse de facturation complète');
            return false;
        }
        if (!sameAddress) {
            const { shipping_address } = formData;
            if (!shipping_address.street || !shipping_address.postal_code || !shipping_address.city) {
                toast.error('Veuillez remplir l\'adresse de livraison complète');
                return false;
            }
        }
        return true;
    };

    const nextStep = () => {
        if (step === 1 && validateStep1()) setStep(2);
        else if (step === 2 && validateStep2()) setStep(3);
        else if (step === 3 && validateStep3()) setStep(4);
        else if (step === 4 && validateStep4()) setStep(5);
    };

    const steps = [
        { number: 1, title: 'Entreprise', icon: Building2 },
        { number: 2, title: 'Contact', icon: User },
        { number: 3, title: 'Activité', icon: Briefcase },
        { number: 4, title: 'Adresses', icon: MapPin },
        { number: 5, title: 'Finaliser', icon: FileText }
    ];

    return (
        <div className="min-h-screen bg-slate-50 py-8 px-4">
            <div className="max-w-2xl mx-auto">
                <Link to="/" className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-8">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Retour à l'accueil
                </Link>

                {/* Step indicator */}
                <nav className="mb-8" aria-label="Progress">
                    <ol className="flex items-center justify-center gap-2 sm:gap-4">
                        {steps.map((s, index) => (
                            <li key={s.number} className="flex items-center">
                                <div className="flex items-center">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                                        step === s.number 
                                            ? 'bg-blue-600 text-white' 
                                            : step > s.number 
                                                ? 'bg-emerald-600 text-white' 
                                                : 'bg-slate-200 text-slate-500'
                                    }`}>
                                        {step > s.number ? <CheckCircle className="w-4 h-4" /> : s.number}
                                    </div>
                                    <span className={`ml-2 text-sm font-medium hidden sm:inline ${
                                        step >= s.number ? 'text-slate-900' : 'text-slate-400'
                                    }`}>
                                        {s.title}
                                    </span>
                                </div>
                                {index < steps.length - 1 && (
                                    <div className={`w-6 sm:w-10 h-0.5 mx-2 sm:mx-3 ${
                                        step > s.number ? 'bg-emerald-600' : 'bg-slate-200'
                                    }`} />
                                )}
                            </li>
                        ))}
                    </ol>
                </nav>

                <Card className="border-slate-200">
                    <CardHeader className="text-center">
                        <CardTitle className="text-2xl" style={{ fontFamily: 'var(--font-heading)' }}>
                            Créer un compte professionnel
                        </CardTitle>
                        <CardDescription>
                            Remplissez le formulaire pour accéder à notre catalogue B2B
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit}>
                            {/* Step 1: Company Info */}
                            {step === 1 && (
                                <div className="space-y-4" data-testid="register-step-1">
                                    <div className="space-y-2">
                                        <Label htmlFor="company_name">Raison sociale *</Label>
                                        <Input
                                            id="company_name"
                                            value={formData.company_name}
                                            onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                                            placeholder="Ma Société SARL"
                                            required
                                            data-testid="register-company-name"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="siren">SIREN / SIRET *</Label>
                                        <Input
                                            id="siren"
                                            value={formData.siren}
                                            onChange={(e) => setFormData({ ...formData, siren: e.target.value.replace(/\D/g, '').slice(0, 14) })}
                                            placeholder="123456789"
                                            required
                                            maxLength={14}
                                            data-testid="register-siren"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="vat_number">N° TVA intracommunautaire</Label>
                                        <Input
                                            id="vat_number"
                                            value={formData.vat_number}
                                            onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                                            placeholder="FR12345678901"
                                            data-testid="register-vat"
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="space-y-2">
                                            <Label htmlFor="country">Pays *</Label>
                                            <Select
                                                value={formData.country}
                                                onValueChange={(value) => setFormData({ ...formData, country: value })}
                                            >
                                                <SelectTrigger data-testid="register-country">
                                                    <SelectValue placeholder="Sélectionnez" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {countries.map((country) => (
                                                        <SelectItem key={country.code} value={country.code}>
                                                            {country.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="city">Ville</Label>
                                            <Input
                                                id="city"
                                                value={formData.city}
                                                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                                                placeholder="Paris"
                                                data-testid="register-city"
                                            />
                                        </div>
                                    </div>

                                    <Button type="button" className="w-full bg-blue-600 hover:bg-blue-700" onClick={nextStep} data-testid="register-next-1">
                                        Continuer
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </div>
                            )}

                            {/* Step 2: Contact Info */}
                            {step === 2 && (
                                <div className="space-y-4" data-testid="register-step-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="contact_name">Nom du contact *</Label>
                                        <Input
                                            id="contact_name"
                                            value={formData.contact_name}
                                            onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                                            placeholder="Jean Dupont"
                                            required
                                            data-testid="register-contact-name"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="email">Email professionnel *</Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            placeholder="contact@entreprise.com"
                                            required
                                            data-testid="register-email"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="phone">Téléphone *</Label>
                                        <Input
                                            id="phone"
                                            type="tel"
                                            value={formData.phone}
                                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                            placeholder="07 25 24 23 22"
                                            required
                                            data-testid="register-phone"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="whatsapp">WhatsApp (optionnel)</Label>
                                        <Input
                                            id="whatsapp"
                                            type="tel"
                                            value={formData.whatsapp}
                                            onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                                            placeholder="+33 7 25 24 23 22"
                                            data-testid="register-whatsapp"
                                        />
                                        <p className="text-xs text-slate-500">Pour recevoir les offres et nouveaux stocks</p>
                                    </div>

                                    <div className="flex gap-3">
                                        <Button type="button" variant="outline" onClick={() => setStep(1)} data-testid="register-back-1">
                                            <ArrowLeft className="w-4 h-4 mr-2" />
                                            Retour
                                        </Button>
                                        <Button type="button" className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={nextStep} data-testid="register-next-2">
                                            Continuer
                                            <ArrowRight className="w-4 h-4 ml-2" />
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Activity & Needs */}
                            {step === 3 && (
                                <div className="space-y-6" data-testid="register-step-3">
                                    {/* Activity Types */}
                                    <div className="space-y-3">
                                        <Label className="text-base font-semibold">Type d'activité *</Label>
                                        <p className="text-sm text-slate-500">Sélectionnez tout ce qui correspond</p>
                                        <div className="grid grid-cols-2 gap-3">
                                            {activityTypes.map((activity) => {
                                                const Icon = activity.icon;
                                                const isSelected = formData.activity_types.includes(activity.id);
                                                return (
                                                    <div
                                                        key={activity.id}
                                                        onClick={() => toggleActivityType(activity.id)}
                                                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                                            isSelected 
                                                                ? 'border-blue-600 bg-blue-50' 
                                                                : 'border-slate-200 hover:border-slate-300'
                                                        }`}
                                                        data-testid={`activity-${activity.id}`}
                                                    >
                                                        <div className="flex items-start gap-2">
                                                            <Icon className={`w-5 h-5 mt-0.5 ${isSelected ? 'text-blue-600' : 'text-slate-400'}`} />
                                                            <div>
                                                                <p className={`font-medium text-sm ${isSelected ? 'text-blue-900' : 'text-slate-900'}`}>
                                                                    {activity.label}
                                                                </p>
                                                                <p className="text-xs text-slate-500">{activity.description}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Search Preference */}
                                    <div className="space-y-3">
                                        <Label className="text-base font-semibold">Vous recherchez principalement… *</Label>
                                        <div className="space-y-2">
                                            {[
                                                { value: 'appareils_reparer', label: 'Des appareils à réparer', desc: 'Cassés, pannes identifiées – écran, batterie, Face ID…' },
                                                { value: 'fonctionnels', label: 'Des appareils fonctionnels prêts à revendre', desc: 'Testés et validés, sans intervention nécessaire' },
                                                { value: 'les_deux', label: 'Les deux selon les opportunités', desc: 'Je m\'adapte selon le prix et la disponibilité' }
                                            ].map((option) => (
                                                <div
                                                    key={option.value}
                                                    onClick={() => setFormData({ ...formData, search_preference: option.value })}
                                                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                                        formData.search_preference === option.value
                                                            ? 'border-blue-600 bg-blue-50'
                                                            : 'border-slate-200 hover:border-slate-300'
                                                    }`}
                                                    data-testid={`search-${option.value}`}
                                                >
                                                    <p className={`font-medium text-sm ${formData.search_preference === option.value ? 'text-blue-900' : 'text-slate-900'}`}>
                                                        {option.label}
                                                    </p>
                                                    <p className="text-xs text-slate-500">{option.desc}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Repair Level */}
                                    <div className="space-y-3">
                                        <Label className="text-base font-semibold">Votre niveau de réparation *</Label>
                                        <p className="text-sm text-slate-500">
                                            Sélectionnez tout ce qui correspond à votre activité
                                        </p>
                                        <div className="grid grid-cols-2 gap-3">
                                            {repairLevels.map((level) => {
                                                const isSelected = formData.repair_levels.includes(level.id);
                                                return (
                                                    <div
                                                        key={level.id}
                                                        onClick={() => toggleRepairLevel(level.id)}
                                                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                                            isSelected
                                                                ? 'border-blue-600 bg-blue-50'
                                                                : 'border-slate-200 hover:border-slate-300'
                                                        }`}
                                                        data-testid={`repair-${level.id}`}
                                                    >
                                                        <p className={`font-semibold text-sm ${isSelected ? 'text-blue-900' : 'text-slate-900'}`}>
                                                            {level.title}
                                                        </p>
                                                        <p className="text-xs text-slate-600 mb-2">{level.subtitle}</p>
                                                        <ul className="text-xs text-slate-500 space-y-0.5">
                                                            {level.items.map((item, i) => (
                                                                <li key={i}>• {item}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Experience & Volume */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Volume estimé / semaine</Label>
                                            <Select
                                                value={formData.weekly_volume}
                                                onValueChange={(value) => setFormData({ ...formData, weekly_volume: value })}
                                            >
                                                <SelectTrigger data-testid="register-volume">
                                                    <SelectValue placeholder="Sélectionnez" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {weeklyVolumes.map((vol) => (
                                                        <SelectItem key={vol.value} value={vol.value}>
                                                            {vol.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Points de vente</Label>
                                            <Select
                                                value={formData.points_of_sale}
                                                onValueChange={(value) => setFormData({ ...formData, points_of_sale: value })}
                                            >
                                                <SelectTrigger data-testid="register-points">
                                                    <SelectValue placeholder="Sélectionnez" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {pointsOfSale.map((pos) => (
                                                        <SelectItem key={pos.value} value={pos.value}>
                                                            {pos.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>

                                    {/* Comments */}
                                    <div className="space-y-2">
                                        <Label htmlFor="comments">Commentaire libre (optionnel)</Label>
                                        <Textarea
                                            id="comments"
                                            value={formData.comments}
                                            onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                                            placeholder="Marques préférées, modèles ciblés, contraintes particulières…"
                                            rows={3}
                                            data-testid="register-comments"
                                        />
                                    </div>

                                    <div className="flex gap-3">
                                        <Button type="button" variant="outline" onClick={() => setStep(2)} data-testid="register-back-2">
                                            <ArrowLeft className="w-4 h-4 mr-2" />
                                            Retour
                                        </Button>
                                        <Button type="button" className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={nextStep} data-testid="register-next-3">
                                            Continuer
                                            <ArrowRight className="w-4 h-4 ml-2" />
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {/* Step 4: Addresses */}
                            {step === 4 && (
                                <div className="space-y-6" data-testid="register-step-4">
                                    {/* Billing Address */}
                                    <div>
                                        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                                            <Building2 className="w-5 h-5 text-blue-600" />
                                            Adresse de facturation *
                                        </h3>
                                        <div className="space-y-4 pl-7">
                                            <div className="space-y-2">
                                                <Label>Adresse *</Label>
                                                <Input
                                                    value={formData.billing_address.street}
                                                    onChange={(e) => updateBillingAddress('street', e.target.value)}
                                                    placeholder="123 rue du Commerce"
                                                    required
                                                    data-testid="billing-street"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Complément d'adresse</Label>
                                                <Input
                                                    value={formData.billing_address.street2}
                                                    onChange={(e) => updateBillingAddress('street2', e.target.value)}
                                                    placeholder="Bâtiment, étage, etc."
                                                    data-testid="billing-street2"
                                                />
                                            </div>
                                            <div className="grid grid-cols-2 gap-3">
                                                <div className="space-y-2">
                                                    <Label>Code postal *</Label>
                                                    <Input
                                                        value={formData.billing_address.postal_code}
                                                        onChange={(e) => updateBillingAddress('postal_code', e.target.value)}
                                                        placeholder="75001"
                                                        required
                                                        data-testid="billing-postal"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Ville *</Label>
                                                    <Input
                                                        value={formData.billing_address.city}
                                                        onChange={(e) => updateBillingAddress('city', e.target.value)}
                                                        placeholder="Paris"
                                                        required
                                                        data-testid="billing-city"
                                                    />
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Pays</Label>
                                                <Select
                                                    value={formData.billing_address.country}
                                                    onValueChange={(value) => updateBillingAddress('country', value)}
                                                >
                                                    <SelectTrigger data-testid="billing-country">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {countries.map((country) => (
                                                            <SelectItem key={country.code} value={country.code}>
                                                                {country.name}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Same address checkbox */}
                                    <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                                        <Checkbox
                                            id="same_address"
                                            checked={sameAddress}
                                            onCheckedChange={(checked) => setSameAddress(checked)}
                                            data-testid="same-address-checkbox"
                                        />
                                        <Label htmlFor="same_address" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                                            <Truck className="w-4 h-4 text-slate-500" />
                                            Adresse de livraison identique
                                        </Label>
                                    </div>

                                    {/* Shipping Address */}
                                    {!sameAddress && (
                                        <div>
                                            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                                                <Truck className="w-5 h-5 text-indigo-600" />
                                                Adresse de livraison *
                                            </h3>
                                            <div className="space-y-4 pl-7">
                                                <div className="space-y-2">
                                                    <Label>Nom / Société</Label>
                                                    <Input
                                                        value={formData.shipping_address.name}
                                                        onChange={(e) => updateShippingAddress('name', e.target.value)}
                                                        placeholder="Nom du destinataire"
                                                        data-testid="shipping-name"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Adresse *</Label>
                                                    <Input
                                                        value={formData.shipping_address.street}
                                                        onChange={(e) => updateShippingAddress('street', e.target.value)}
                                                        placeholder="456 avenue de la Livraison"
                                                        required={!sameAddress}
                                                        data-testid="shipping-street"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Complément</Label>
                                                    <Input
                                                        value={formData.shipping_address.street2}
                                                        onChange={(e) => updateShippingAddress('street2', e.target.value)}
                                                        placeholder="Bâtiment, étage, etc."
                                                        data-testid="shipping-street2"
                                                    />
                                                </div>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div className="space-y-2">
                                                        <Label>Code postal *</Label>
                                                        <Input
                                                            value={formData.shipping_address.postal_code}
                                                            onChange={(e) => updateShippingAddress('postal_code', e.target.value)}
                                                            placeholder="69001"
                                                            required={!sameAddress}
                                                            data-testid="shipping-postal"
                                                        />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>Ville *</Label>
                                                        <Input
                                                            value={formData.shipping_address.city}
                                                            onChange={(e) => updateShippingAddress('city', e.target.value)}
                                                            placeholder="Lyon"
                                                            required={!sameAddress}
                                                            data-testid="shipping-city"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Pays</Label>
                                                    <Select
                                                        value={formData.shipping_address.country}
                                                        onValueChange={(value) => updateShippingAddress('country', value)}
                                                    >
                                                        <SelectTrigger data-testid="shipping-country">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {countries.map((country) => (
                                                                <SelectItem key={country.code} value={country.code}>
                                                                    {country.name}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex gap-3">
                                        <Button type="button" variant="outline" onClick={() => setStep(3)} data-testid="register-back-3">
                                            <ArrowLeft className="w-4 h-4 mr-2" />
                                            Retour
                                        </Button>
                                        <Button type="button" className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={nextStep} data-testid="register-next-4">
                                            Continuer
                                            <ArrowRight className="w-4 h-4 ml-2" />
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {/* Step 5: Finalize */}
                            {step === 5 && (
                                <div className="space-y-6" data-testid="register-step-5">
                                    {/* Password */}
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="password">Mot de passe *</Label>
                                            <Input
                                                id="password"
                                                type="password"
                                                value={formData.password}
                                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                                placeholder="Votre mot de passe"
                                                required
                                                data-testid="register-password"
                                            />
                                            <p className="text-xs text-slate-500">Minimum 8 caractères</p>
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="confirmPassword">Confirmer le mot de passe *</Label>
                                            <Input
                                                id="confirmPassword"
                                                type="password"
                                                value={formData.confirmPassword}
                                                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                                placeholder="Confirmez votre mot de passe"
                                                required
                                                data-testid="register-confirm-password"
                                            />
                                        </div>
                                    </div>

                                    {/* Calendly Section */}
                                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                        <div className="flex items-start gap-3">
                                            <Calendar className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                                            <div className="flex-1">
                                                <h4 className="font-semibold text-blue-900 mb-1">Appel de bienvenue (optionnel)</h4>
                                                <p className="text-sm text-blue-700 mb-3">
                                                    15 min pour vous présenter notre solution et vous accompagner au mieux pour répondre à vos besoins.
                                                </p>
                                                <a
                                                    href="https://calendly.com/s-ecomobilesolution/30min"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                                                    data-testid="calendly-link"
                                                >
                                                    <Calendar className="w-4 h-4" />
                                                    Réserver un créneau
                                                    <ExternalLink className="w-3 h-3" />
                                                </a>
                                                <p className="text-xs text-blue-600 mt-2">
                                                    Sinon, nous vous contacterons directement.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Conditions & Communications */}
                                    <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                                        <h4 className="font-semibold text-slate-900">Conditions & communications</h4>
                                        
                                        <p className="text-xs text-slate-500">
                                            En soumettant ce formulaire, vous acceptez que vos coordonnées soient utilisées à des fins commerciales dans le cadre de notre relation B2B.
                                        </p>

                                        <div className="space-y-3">
                                            <div className="flex items-start space-x-3">
                                                <Checkbox
                                                    id="accept_cgv"
                                                    checked={formData.accept_cgv}
                                                    onCheckedChange={(checked) => setFormData({ ...formData, accept_cgv: checked })}
                                                    data-testid="accept-cgv-checkbox"
                                                />
                                                <Label htmlFor="accept_cgv" className="text-sm cursor-pointer leading-relaxed">
                                                    J'ai lu et j'accepte les{' '}
                                                    <a href="/cgv" target="_blank" className="text-blue-600 hover:underline">
                                                        conditions générales de vente
                                                    </a>{' '}
                                                    et la{' '}
                                                    <a href="/privacy" target="_blank" className="text-blue-600 hover:underline">
                                                        politique de confidentialité
                                                    </a>. *
                                                </Label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex gap-3">
                                        <Button type="button" variant="outline" onClick={() => setStep(4)} data-testid="register-back-4">
                                            <ArrowLeft className="w-4 h-4 mr-2" />
                                            Retour
                                        </Button>
                                        <Button 
                                            type="submit" 
                                            className="flex-1 bg-blue-600 hover:bg-blue-700" 
                                            disabled={loading || !formData.accept_cgv} 
                                            data-testid="register-submit"
                                        >
                                            {loading ? 'Envoi...' : 'Soumettre ma demande'}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </form>

                        <div className="mt-6 text-center text-sm text-slate-600">
                            Déjà un compte ?{' '}
                            <Link to="/login" className="text-blue-600 hover:underline font-medium" data-testid="register-login-link">
                                Se connecter
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default RegisterPage;
